import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { TestedUnitsSyncService } from './tested-units-sync.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
} from '../../../shared/workspace-element-context';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';

@Component({
  selector: 'gtw-tested-units-sync',
  templateUrl: './tested-units-sync.component.html',
  styleUrls: ['./tested-units-sync.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class TestedUnitsSyncComponent implements OnInit, OnDestroy {

  /** Legacy AngularJS bridge (firTestedUnitsCtrl). */
  @Output('close-firtestedunitsconfirm-info')
  closefirTestedUnitsconfirmInfo = new EventEmitter<string>();

  @Output('save-firtestedunitssync-info')
  savefirTestedUnitsSyncInfo = new EventEmitter<string>();

  /** Consumed by CeHostDialogComponent in the migrated grid flow. */
  @Output('request-grid-update')
  requestGridUpdate = new EventEmitter<string>();

  showModal = true;
  modalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to sync the Tested Units?',
    iconClass: 'Appkit4-icon icon-logout-fill',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'Cancel',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...',
  };

  baseGlobalParams: {
    jcd_key?: string | number;
    tax_year?: string | number;
    scenario?: string | number;
    client_key?: string | number;
    scenarioCode?: string;
    screen_key?: string | number;
    filing_key?: string | number;
    checkLockedbj?: any;
    modalScreen?: { modal_name?: string };
  } | undefined;

  baseURLs: any = { admin: '', api: '/gtw', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };

  clob_settings: {
    tax_year: number;
    scenario: number;
    jcd_key: number;
    parent_me_string: string;
    ho_leid_opt: string;
    filing_key: number;
  } = {
    tax_year: 0,
    scenario: 0,
    jcd_key: 0,
    parent_me_string: '',
    ho_leid_opt: '',
    filing_key: 0,
  };
  clob_data: any = [];

  private ssoId = '';
  private checkLockedObj: any = [];
  private filterParams: any = {};
  private modalName = '';
  private saveRequestSeq = 0;

  subscriptions: Subscription = new Subscription();

  constructor(
    private testedUnitsSyncService: TestedUnitsSyncService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
  ) {}

  checkInputType(): boolean {
    return Number.isInteger(parseInt(this.ssoId + '', 10));
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.init();
    this.subscriptions.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      }),
    );
    this.subscriptions.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.baseGlobalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    this.filterParams = normalizeFilterParams(activeScreen) || {};
    this.buildCheckLockedObj();
    this.modalName = this.baseGlobalParams?.modalScreen?.modal_name || this.modalName;
    const sso = this.gtwApp?.USER_SETTINGS?.user?.sso_id;
    this.ssoId = sso != null && sso !== '' ? String(sso) : this.ssoId;
    this.cdr.markForCheck();
  }

  /**
   * Legacy parity: `firTestedUnitsCtrl.js` passed
   * `[{ scenario_key, tax_year, scenarioCode, is_issue_key_enabled: 'N' }]` as check-locked-obj.
   */
  private buildCheckLockedObj(): void {
    const fromGlobal = this.baseGlobalParams?.checkLockedbj;
    if (fromGlobal != null && fromGlobal !== '') {
      this.checkLockedObj = fromGlobal;
      return;
    }
    this.checkLockedObj = [{
      scenario_key: this.filterParams.scenario,
      tax_year: this.filterParams.tax_year,
      scenarioCode: this.baseGlobalParams?.scenarioCode,
      is_issue_key_enabled: 'N',
    }];
  }

  init(): void {
    if (this.checkInputType()) {
      // No-op; placeholder retained for parity with AngularJS controller.
    }
  }

  onCancel(): void {
    this.showModal = false;
    this.closefirTestedUnitsconfirmInfo.emit('close-firtestedunitsconfirm-info');
    this.requestGridUpdate.emit('cancel');
    this.cdr.markForCheck();
  }

  onConfirm(): void {
    this.modalConfig = { ...this.modalConfig, loading: true };
    this.cdr.markForCheck();

    const processName = 'RUN_SYNC_TESTED_UNITS';

    this.clob_settings = {
      tax_year: this.filterParams.tax_year,
      scenario: this.filterParams.scenario,
      jcd_key: this.filterParams.jcd_key,
      parent_me_string:
        this.filterParams.me_string == undefined ? null as any : this.filterParams.me_string,
      ho_leid_opt:
        this.filterParams.ho_leid_opt == undefined ? null as any : this.filterParams.ho_leid_opt,
      filing_key: this.filterParams.filing_key,
    };

    const tempData = {
      TAX_YEAR: this.filterParams?.tax_year,
      SCENARIO: this.filterParams?.scenario,
      JCD_KEY: this.baseGlobalParams?.jcd_key,
      SSO_ID: this.ssoId,
    };

    const seq = ++this.saveRequestSeq;
    const updateSubscription = this.testedUnitsSyncService.synctestedUnits(
      this.baseURLs.api,
      tempData,
      this.clob_settings,
      this.clob_data,
      processName,
      this.baseGlobalParams,
      this.checkLockedObj,
    ).subscribe(
      (data: any) => {
        if (seq !== this.saveRequestSeq) {
          return;
        }
        if (data.callSuccess == '1') {
          this.testedUnitsSyncService.openSnackBar(
            'Data Synced Successfully',
            'gtw-snackbar--green',
          );
          this.savefirTestedUnitsSyncInfo.emit('save-firtestedunitssync-info');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.testedUnitsSyncService.openSnackBar(
            'Error syncing Data. ' + data.errorMessage,
            'gtw-snackbar--red',
          );
          this.modalConfig = { ...this.modalConfig, loading: false };
          this.closefirTestedUnitsconfirmInfo.emit('close-firtestedunitsconfirm-info');
          this.requestGridUpdate.emit('cancel');
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        if (seq !== this.saveRequestSeq) {
          return;
        }
        this.testedUnitsSyncService.openSnackBar(
          'Error syncing Data. ' + error?.error?.errorMessage,
          'gtw-snackbar--red',
        );
        this.modalConfig = { ...this.modalConfig, loading: false };
        this.cdr.markForCheck();
      },
    );

    this.subscriptions.add(updateSubscription);
  }
}
